/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { theme } from "@nordcloud/gnui";
import {
  ResourceExecutionErrorCode,
  ResourceExecutionErrorCodeCount,
} from "~/generated/graphql";
import { LOG_OUTPUT_LABELS, LOG_RELATED_ERROR_CODES } from "~/constants";
import type { SvgName } from "~/tools/types";

type LogActionDescription = {
  text: string;
  icon?: SvgName;
  iconColor?: string;
};

const logActionDescriptionsByErrorCode: Partial<
  Record<ResourceExecutionErrorCode, LogActionDescription>
> = {
  [ResourceExecutionErrorCode.LogsMalformed]: {
    text: LOG_OUTPUT_LABELS.malformed,
    icon: "danger",
    iconColor: theme.colors.danger,
  },
  [ResourceExecutionErrorCode.LogsNotAvailable]: {
    text: LOG_OUTPUT_LABELS.unavailable,
    icon: "danger",
    iconColor: theme.colors.danger,
  },
};

export function hasLogRelatedErrorCode(
  errorCode?: ResourceExecutionErrorCode | null
) {
  return LOG_RELATED_ERROR_CODES.some(
    (logRelatedErrorCode) => logRelatedErrorCode === errorCode
  );
}

export function hasLogRelatedErrorCodeCounts(
  errorCodeCounts: ResourceExecutionErrorCodeCount[] = []
) {
  return errorCodeCounts.some(
    ({ errorCode, count }) => count > 0 && hasLogRelatedErrorCode(errorCode)
  );
}

export function getLogActionDescription(
  errorCode?: ResourceExecutionErrorCode | null
): LogActionDescription | undefined {
  if (!errorCode) {
    return undefined;
  }

  return logActionDescriptionsByErrorCode[errorCode];
}

export function getLogActionDescriptionFromCounts(
  errorCodeCounts: ResourceExecutionErrorCodeCount[] = []
): LogActionDescription | undefined {
  const matchingErrorCode = LOG_RELATED_ERROR_CODES.find((errorCode) =>
    errorCodeCounts.some(
      (errorCodeCount) =>
        errorCodeCount.count > 0 && errorCodeCount.errorCode === errorCode
    )
  );

  return getLogActionDescription(matchingErrorCode);
}
